import { useParams } from "@tanstack/react-router"
import { useCallback } from "react"
import type { RefObject } from "react"
import type { Client } from "urql"
import { useShallow } from "zustand/react/shallow"

import { CUSTOMER_PURCHASE_ORDER_FILTER_KEYS } from "../constants"
import { useCustomerPurchaseOrderSearchStore } from "../stores/useCustomerPurchaseOrderSearchStore"

import { useDeleteCustomerPurchaseOrder } from "./useDeleteCustomerPurchaseOrder"

import type { CustomerPurchaseOrderListItem } from "@/graphql/codegen/graphql"
import { useListPageBase } from "@/hooks/useListPageBase"
import type { CustomerPurchaseOrderSearchParams } from "@/screens/CustomerOrders/schemas"

const ROUTE = "/app/orders/$companyId/$shipToLocationId/_layout/customer-purchase-orders"

interface UseCustomerPurchaseOrderListReturn {
  cpos: CustomerPurchaseOrderListItem[]
  isFetching: boolean
  error: Error | undefined
  searchParams: CustomerPurchaseOrderSearchParams
  tableContainerRef: RefObject<HTMLDivElement>
  handleScroll: () => void
  handleSearchChange: (query: string) => void
  handleFilterChange: <K extends keyof CustomerPurchaseOrderSearchParams>(
    key: K,
    value: CustomerPurchaseOrderSearchParams[K]
  ) => void
  handleFiltersChange: (updates: Partial<CustomerPurchaseOrderSearchParams>) => void
  handleFilterRemove: (
    keyOrKeys: keyof CustomerPurchaseOrderSearchParams | (keyof CustomerPurchaseOrderSearchParams)[],
    value?: string | number
  ) => void
  handleFilterClear: () => void
  handleDeleteCpo: (customerPurchaseOrderId: string) => Promise<boolean>
  refetch: () => void
}

export const useCustomerPurchaseOrderList = (): UseCustomerPurchaseOrderListReturn => {
  const { companyId, shipToLocationId } = useParams({ from: ROUTE })

  const store = useCustomerPurchaseOrderSearchStore(
    useShallow(({ data, fetching, error, executeSearch, loadMore, clearStore }) => ({
      data,
      fetching,
      error,
      executeSearch,
      loadMore,
      clearStore,
    }))
  )

  const { executeSearch: storeExecuteSearch, loadMore: storeLoadMore } = store

  // Depend on the store's action functions directly, not the whole `store` object — `store` is
  // a new reference every time data/fetching/error change (useShallow selects them together),
  // which would give this callback a new identity on every fetch and retrigger
  // useListPageBase's fetch effect in a loop. The action functions themselves never change.
  const executeSearch = useCallback(
    (client: Client, params: CustomerPurchaseOrderSearchParams, force?: boolean) => {
      storeExecuteSearch(client, { ...params, companyId, shipToLocationId }, force)
    },
    [storeExecuteSearch, companyId, shipToLocationId]
  )

  const loadMore = useCallback(
    (client: Client, params: CustomerPurchaseOrderSearchParams) => {
      storeLoadMore(client, { ...params, companyId, shipToLocationId })
    },
    [storeLoadMore, companyId, shipToLocationId]
  )

  const { deleteCustomerPurchaseOrder } = useDeleteCustomerPurchaseOrder()

  const {
    data: cpos,
    isFetching,
    error,
    searchParams,
    tableContainerRef,
    handleScroll,
    handleSearchChange,
    handleFilterChange,
    handleFiltersChange,
    handleFilterRemove,
    handleFilterClear,
    refetch,
  } = useListPageBase<CustomerPurchaseOrderListItem, CustomerPurchaseOrderSearchParams>({
    route: ROUTE,
    store: { ...store, executeSearch, loadMore },
    filterKeys: CUSTOMER_PURCHASE_ORDER_FILTER_KEYS,
  })

  const handleDeleteCpo = useCallback(
    async (customerPurchaseOrderId: string): Promise<boolean> => {
      const success = await deleteCustomerPurchaseOrder(customerPurchaseOrderId)
      if (success) refetch()
      return success
    },
    [deleteCustomerPurchaseOrder, refetch]
  )

  return {
    cpos,
    isFetching,
    error,
    searchParams,
    tableContainerRef,
    handleScroll,
    handleSearchChange,
    handleFilterChange,
    handleFiltersChange,
    handleFilterRemove,
    handleFilterClear,
    handleDeleteCpo,
    refetch,
  }
}
